import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../core/services/settings.service';
import { UnitOfMeasure } from '../../core/models/settings.models';
import { confirmAction } from '../../core/utils/confirm.util';

@Component({
  selector: 'app-units',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './units-of-measure.component.html',
  styleUrls: ['../../settings-shared.css', './units-of-measure.component.css'],
})
export class UnitsOfMeasureComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');

  protected unitForm: Omit<UnitOfMeasure, 'id'> = {
    code: '',
    name: '',
    abbreviation: '',
    status: 'active',
  };

  protected readonly units = this.settings.allUnits;

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: UnitOfMeasure): void {
    this.editingId.set(row.id);
    this.unitForm = {
      code: row.code,
      name: row.name,
      abbreviation: row.abbreviation,
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.unitForm.name?.trim()) {
      this.toastMessage.set('Name is required');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    if (editingId) {
      this.settings.updateUnit(editingId, this.unitForm).subscribe((result) => {
        this.toastMessage.set(result.message);
        this.showToast.set(true);
        setTimeout(() => this.showToast.set(false), 4000);
        if (result.success) {
          this.closeModal();
        }
      });
      return;
    }

    this.settings.addUnit(this.unitForm);
    this.toastMessage.set('Unit added successfully');
    this.closeModal();
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  deleteRow(row: UnitOfMeasure): void {
    if (!confirmAction(`Delete "${row.name}"? This cannot be undone.`)) {
      return;
    }

    this.settings.deleteUnit(row.id).subscribe((result) => {
      this.toastMessage.set(result.message);
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
    });
  }

  private resetForm(): void {
    this.unitForm = {
      code: '',
      name: '',
      abbreviation: '',
      status: 'active',
    };
  }
}
